import {FC, Suspense} from 'react';
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom';
//utils
import {ROUTER_URL} from '@/utils/enums';
//layouts
import UserTemplate from '@/layouts/UserTemplate';
import OrganizationTemplate from '@/layouts/OrganizationTemplate';
import AdminTemplate from '@/layouts/AdminTemplate';
import ProfileTemplate from '@/layouts/ProfileTemplate';
//pages
import {default as Dashboard} from '@/pages/Organization/Dashboard';
import {default as UserHomePage} from '@/pages/User/Home';
import PrivacyPolicy from '@/pages/Organization/PrivacyPolicy';
import OrganizationSignUp from '@/pages/Organization/OrganizationSignUp';
import OrganizationLogin from '@/pages/Organization/OrganizationLogin';
import TodoListPage from '@/pages/Organization/Dashboard/TodoListPage';
import OrganizationPrivateRoute from './Organization/OrganizationPrivateRoute';
import OrganizationAuthPrivateRoute from './Organization/OrganizationAuthPrivateRoute';
import TablesAndTickets from '@/pages/Organization/TablesAndTickets';
import Attendees from '@/pages/Organization/Attendees';
import Auction from '@/pages/Organization/Auction';
import OrganizationProfileGeneral from '@/pages/Organization/Profile/General';
import ProfileBilling from '@/pages/Organization/Profile/Billing';
import ProfileTeam from '@/pages/Organization/Profile/Team';
import ProfileContactList from '@/pages/Organization/Profile/ContactList';
import Reports from '@/pages/Organization/Reports';
import NotificationsSettingsGeneral from '@/pages/Organization/Profile/NotificationsSettings';
import NotificationsPage from '@/pages/Organization/Notifications';
import EventSettings from '@/pages/Organization/EventSettings';
import Integration from '@/pages/Organization/Profile/Integration';
import HelpAndSupport from '@/pages/HelpAndSupport';
import UserPrivateRoute from './User/UserPrivatRoute';
import Customization from '@/pages/Organization/Printables/Customization';
import QRCode from '@/pages/Organization/Printables/QRCode';
import BidSheets from '@/pages/Organization/Printables/BidSheets';
import GuestByName from '@/pages/Organization/Printables/GuestByName';
import GuestByTable from '@/pages/Organization/Printables/GuestByTable';
import UserProfileGeneral from '@/pages/User/Profile/General';
import UserRaffle from '@/pages/User/Raffle';
import AllItems from '@/pages/User/Auction/AllItems';
import ComunicationCustomization from '@/pages/Organization/Communications/Customisation';
import TicketInvoice from '@/pages/Organization/Communications/TicketInvoice';
import TicketPaymentConfirmation from '@/pages/Organization/Communications/TicketPaymentConfirmation';
import TableUpdateEmail from '@/pages/Organization/Communications/TableUpdateEmail';
import PatronUpdateEmail from '@/pages/Organization/Communications/PatronUpdateEmail';
import InvoiceEmail from '@/pages/Organization/Communications/InvoiceEmail';
import PaymentAcknowledgement from '@/pages/Organization/Communications/PaymentAcknowledgement';
import ContributionsSummary from '@/pages/Organization/Communications/ContributionsSummary';
import AdminProfileGeneral from '@/pages/Admin/Profile/General';
import AdminBillingHistory from '@/pages/Admin/Profile/Billing';
import AdminDashboard from '@/pages/Admin/Dashboard';
import Organizations from '@/pages/Admin/Organizations';
import Events from '@/pages/Admin/Events';
import Contributions from '@/pages/User/Profile/Contributions';
import AuctionEmail from '@/pages/Organization/Communications/AuctionEmail';
import LiveAuction from '@/pages/User/Auction/LiveAuction';
import SilentAuction from '@/pages/User/Auction/SilentAuction';
import ItemDetailPage from '@/pages/User/ItemDetailPage';
import UserFund from '@/pages/User/Fund';
import AdminPrivateRoute from './Admin/AdminPrivatRoute';
import AccountSettings from '@/pages/Organization/Profile/AccountSettings';
import AttendeeDetailsPage from '@/pages/Organization/AttendeeDetails';
import PurchaseTicketsPage from '@/pages/Organization/PurchaseTickets';
import FundTheMission from '@/pages/Organization/Fund';
import AuctionEntry from '@/pages/Organization/AuctionEntry';
import AddWinnerPage from '@/pages/Organization/AddWinnerPage';
import WinnersDetailsPage from '@/pages/Organization/WinnerDetailsPage';
import CheckOut from '@/pages/Organization/CheckOut';
import AddEventPage from '@/pages/Organization/AddEvent';
import UserAccountSettings from '@/pages/User/UserAccountSettings';
import UserRaffleBuyTicketPage from '@/pages/User/UserRaffleBuyTicket';
import TableDetailsPage from '@/pages/Organization/TableDetailsPage';
import ResetPasswordHandler from '@/shared/components/ResetPassword';
import {ScrollToTop} from '@/shared/components/ScrollTo';
import MyTablePage from '@/pages/User/MyTable';
import TermsAndConditions from '@/pages/Organization/Terms&Conditions';
import PrivatePackageEntry from './User/PrivatePackageEntry';
import UserHelpAndSupport from '@/pages/User/HelpAndSupport';

const Navigator: FC = () => {
  return (
    <Suspense fallback={<div>loader here</div>}>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* organization */}
          <Route path={ROUTER_URL.RESET_PASSWORD} element={<ResetPasswordHandler />} />
          <Route
            path={ROUTER_URL.ORGANIZATION}
            element={
              <OrganizationPrivateRoute>
                <OrganizationTemplate />
              </OrganizationPrivateRoute>
            }
          >
            <Route index element={<Navigate to={ROUTER_URL.ORGANIZATION_DASHBOARD} replace />} />
            <Route path={ROUTER_URL.ORGANIZATION_DASHBOARD} element={<Dashboard />} />
            <Route path={ROUTER_URL.ORGANIZATION_TABLES} element={<TablesAndTickets />} />
            <Route path={`${ROUTER_URL.ORGANIZATION_TABLES_DETAILS}/:id`} element={<TableDetailsPage />} />
            <Route path={ROUTER_URL.ORGANIZATION_PURCHASE_TICKETS} element={<PurchaseTicketsPage />} />
            <Route path={ROUTER_URL.ORGANIZATION_ADD_EVENT} element={<AddEventPage />} />

            <Route path={ROUTER_URL.ORGANIZATION_ATTENDEES} element={<Attendees />} />
            <Route path={`${ROUTER_URL.ORGANIZATION_ATTENDEES}/:id`} element={<AttendeeDetailsPage />} />
            <Route path={ROUTER_URL.ORGANIZATION_REPORTS} element={<Reports />} />

            <Route
              path={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_CUSTOMIZATION}
              element={<ComunicationCustomization />}
            />
            <Route path={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_TICKETS} element={<TicketInvoice />} />
            <Route
              path={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_TICKET_PAYMENTS}
              element={<TicketPaymentConfirmation />}
            />
            <Route path={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_TABLE_UPDATE} element={<TableUpdateEmail />} />
            <Route path={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_PATRON_UPDATE} element={<PatronUpdateEmail />} />
            <Route path={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_AUCTION_EMAIL} element={<AuctionEmail />} />
            <Route path={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_INVOISE_EMAIL} element={<InvoiceEmail />} />
            <Route
              path={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_PAYMENT_ACKNOWLEDGEMENT}
              element={<PaymentAcknowledgement />}
            />
            <Route
              path={ROUTER_URL.ORGANIZATION_COMMUNICATIONS_CONTRIBUTIONS_SUMMARY}
              element={<ContributionsSummary />}
            />
            <Route path={ROUTER_URL.ORGANIZATION_PRINTABLES_CUSTOMIZATION} element={<Customization />} />
            <Route path={ROUTER_URL.ORGANIZATION_PRINTABLES_QR} element={<QRCode />} />
            <Route path={ROUTER_URL.ORGANIZATION_PRINTABLES_BID_SHEETS} element={<BidSheets />} />
            <Route path={ROUTER_URL.ORGANIZATION_PRINTABLES_GUESTS_BY_TABLE} element={<GuestByTable />} />
            <Route path={ROUTER_URL.ORGANIZATION_PRINTABLES_GUESTS_BY_NAME} element={<GuestByName />} />

            <Route path={ROUTER_URL.ORGANIZATION_EVENT_SETTINGS} element={<EventSettings />} />

            <Route path={ROUTER_URL.ORGANIZATION_FUND} element={<FundTheMission />} />
            <Route path={ROUTER_URL.ORGANIZATION_AUCTION_ENTRY} element={<AuctionEntry />} />
            <Route path={ROUTER_URL.ORGANIZATION_CHECK_OUT} element={<CheckOut />} />

            <Route path={ROUTER_URL.ORGANIZATION_AUCTION_ADD_WINNER} element={<AddWinnerPage />} />
            <Route path={`${ROUTER_URL.ORGANIZATION_WINNER_PROFILE}/:id`} element={<WinnersDetailsPage />} />

            <Route path={ROUTER_URL.ORGANIZATION_AUCTION} element={<Auction />} />
            <Route path={ROUTER_URL.ORGANIZATION_DASHBOARD_TODO_LIST} element={<TodoListPage />} />
            <Route path={ROUTER_URL.ORGANIZATION_NOTIFICATIONS} element={<NotificationsPage />} />

            <Route path={ROUTER_URL.ORGANIZATION_ACCOUNT_SETTINGS} element={<AccountSettings />} />
            <Route path={ROUTER_URL.ORGANIZATION_PROFILE} element={<ProfileTemplate />}>
              <Route index element={<Navigate to={ROUTER_URL.ORGANIZATION_PROFILE_GENERAL} replace />} />
              <Route path={ROUTER_URL.ORGANIZATION_PROFILE_GENERAL} element={<OrganizationProfileGeneral />} />
              <Route path={ROUTER_URL.ORGANIZATION_PROFILE_BILLING} element={<ProfileBilling />} />
              <Route path={ROUTER_URL.ORGANIZATION_PROFILE_NOTIFICATION} element={<NotificationsSettingsGeneral />} />
              <Route path={ROUTER_URL.ORGANIZATION_PROFILE_TEAM} element={<ProfileTeam />} />
              <Route path={ROUTER_URL.ORGANIZATION_PROFILE_CONTACT_LIST} element={<ProfileContactList />} />
              <Route path={ROUTER_URL.ORGANIZATION_PROFILE_INTEGRATION} element={<Integration />} />
            </Route>
            <Route path={ROUTER_URL.ORGANIZATION_HELP_AND_SUPPORT} element={<HelpAndSupport />} />
          </Route>
          {/* user */}
          <Route
            path="/:slug"
            element={
              <UserPrivateRoute>
                <UserTemplate />
              </UserPrivateRoute>
            }
          >
            {/* /user/:id */}
            <Route index element={<UserHomePage />} />
            <Route path={ROUTER_URL.USER_HELP_AND_SUPPORT} element={<UserHelpAndSupport />} />
            <Route path={ROUTER_URL.USER_TABLES} element={<MyTablePage />} />
            <Route path={`${ROUTER_URL.USER_TABLES_DETAILS}/:id`} element={<TableDetailsPage />} />

            <Route path={ROUTER_URL.USER_NOTIFICATIONS} element={<NotificationsPage />} />
            <Route path={ROUTER_URL.USER_ALL_ITEMS} element={<AllItems />} />
            <Route path={ROUTER_URL.USER_SILENT_AUCTION} element={<SilentAuction />} />
            <Route path={ROUTER_URL.USER_LIVE_AUCTION} element={<LiveAuction />} />
            <Route path={ROUTER_URL.USER_RAFFLE} element={<UserRaffle />} />
            <Route path={ROUTER_URL.USER_RAFFLE_BUY_TICKET} element={<UserRaffleBuyTicketPage />} />
            <Route path={ROUTER_URL.USER_FUND_MISSIONS} element={<UserFund />} />
            <Route path="details/:id" element={<ItemDetailPage />} />
            <Route path={ROUTER_URL.USER_PURCHASE_TICKETS} element={<PurchaseTicketsPage />} />

            {/* profile */}
            <Route path={ROUTER_URL.USER_ACCOUNT_SETTINGS} element={<UserAccountSettings />} />
            <Route path={ROUTER_URL.PROFILE} element={<ProfileTemplate />}>
              <Route index element={<Navigate to={ROUTER_URL.PROFILE_GENERAL} replace />} />
              <Route path={ROUTER_URL.PROFILE_GENERAL} element={<UserProfileGeneral />} />
              <Route path={ROUTER_URL.PROFILE_NOTIFICATION} element={<NotificationsSettingsGeneral />} />
              <Route path={ROUTER_URL.PROFILE_CONTRIBUTIONS} element={<Contributions />} />
            </Route>
          </Route>

          {/* admin */}
          <Route
            path={`${ROUTER_URL.ADMIN}`}
            element={
              <AdminPrivateRoute>
                <AdminTemplate />
              </AdminPrivateRoute>
            }
          >
            <Route path={ROUTER_URL.ADMIN_DASHBOARD} element={<AdminDashboard />} />
            <Route path={ROUTER_URL.ADMIN_ORGANIZATION} element={<Organizations />} />
            <Route path={ROUTER_URL.ADMIN_EVENTS} element={<Events />} />
            <Route path={ROUTER_URL.PROFILE_NOTIFICATION} element={<NotificationsPage />} />

            <Route path={ROUTER_URL.PROFILE} element={<ProfileTemplate />}>
              <Route index element={<Navigate to={ROUTER_URL.PROFILE_GENERAL} replace />} />
              <Route path={ROUTER_URL.PROFILE_GENERAL} element={<AdminProfileGeneral />} />
              <Route path={ROUTER_URL.PROFILE_NOTIFICATION} element={<NotificationsSettingsGeneral />} />
              <Route path={ROUTER_URL.ADMIN_PROFILE_BILLING} element={<AdminBillingHistory />} />
            </Route>
          </Route>
          {/* auth */}
          <Route
            path={ROUTER_URL.LOGIN}
            element={
              <OrganizationAuthPrivateRoute>
                <OrganizationLogin />
              </OrganizationAuthPrivateRoute>
            }
          />
          <Route
            path={ROUTER_URL.SIGN_UP}
            element={
              <OrganizationAuthPrivateRoute>
                <OrganizationSignUp />
              </OrganizationAuthPrivateRoute>
            }
          />
          <Route path={ROUTER_URL.ORGANIZATION_PRIVACY_POLICY} element={<PrivacyPolicy />} />
          <Route path={ROUTER_URL.ORGANIZATION_TERMS_AND_CONDITIONS} element={<TermsAndConditions />} />
          <Route path={`${ROUTER_URL.PRIVATE_PACKAGE}/:eventId/:token`} element={<PrivatePackageEntry />} />
          <Route path="*" element={<Navigate to={ROUTER_URL.ORGANIZATION} />} />
        </Routes>
      </BrowserRouter>
    </Suspense>
  );
};

export default Navigator;
